import * as React from 'react';
import { useState } from 'react';
import moment from 'moment-timezone';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import AreaShell from '../../chrome/AreaShell';
import { ConfirmDialog, Dialog, Field, TextInput } from '../../components/forms';
import { ListState, useList, useToast } from '../../components/roster';
import { TCard, TFoot, TRow } from '../../components/table';
import './org.css';

/*
 * Org email templates: HTML for outgoing share emails, imported as a
 * zip (index.html + images/). Same calls as the v2 page.
 */

interface TemplateRow {
    id: number;
    name: string;
    body?: string;
    createdAt?: string;
}

export default function OrgTemplatesV3() {
    const { orgId } = useUserOrg();
    const api = useAuthenticatedApi();
    const toast = useToast();

    const { rows, setRows, failed, reload } = useList<TemplateRow>(
        () => (orgId ? api.get(`${orgId}/templates`).then((r: any) => r.data ?? []) : null),
        [orgId],
    );

    const [preview, setPreview] = useState<TemplateRow | null>(null);
    const [removing, setRemoving] = useState<TemplateRow | null>(null);

    const [importOpen, setImportOpen] = useState(false);
    const [importName, setImportName] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [tried, setTried] = useState(false);

    const openImport = () => {
        setImportName('');
        setImportFile(null);
        setImportError('');
        setTried(false);
        setImportOpen(true);
    };
    const closeImport = () => {
        if (!importing) setImportOpen(false);
    };

    const nameError = tried && !importName.trim() ? 'Give the template a name.' : undefined;
    const fileError = tried && !importFile ? 'Choose a .zip file.' : undefined;

    const submitImport = async () => {
        if (importing) return;
        setTried(true);
        if (!importName.trim() || !importFile) return;
        setImporting(true);
        setImportError('');
        const formData = new FormData();
        formData.append('zip', importFile);
        formData.append('name', importName.trim());
        formData.append('orgId', String(orgId));
        const ok = await api
            .post(`${orgId}/templates/import`, formData)
            .then(() => true)
            .catch(() => false);
        setImporting(false);
        if (ok) {
            setImportOpen(false);
            toast.show(`Imported “${importName.trim()}”.`);
            reload();
        } else {
            setImportError('Import failed — check the zip has index.html at its root and the name is unique.');
        }
    };

    const remove = async (row: TemplateRow) => {
        const prev = rows;
        setRows((cur) => (cur ?? []).filter((r) => r.id !== row.id));
        const ok = await api
            .delete(`${orgId}/templates/${row.id}`)
            .then(() => true)
            .catch(() => false);
        if (ok) {
            toast.show(`Deleted “${row.name}”.`);
        } else {
            setRows(prev);
            toast.show(`Couldn’t delete “${row.name}”.`);
        }
    };

    const count = rows?.length ?? 0;

    return (
        <AreaShell
            area="org"
            tab="templates"
            sub="HTML templates for outgoing share emails"
            actions={
                <button type="button" className="tb-new" onClick={openImport}>
                    Import template
                </button>
            }
        >
            <div className="org-pg">
                <ListState
                    rows={rows}
                    failed={failed}
                    what="email templates"
                    reload={reload}
                    empty={rows && rows.length === 0 ? 'No templates yet — share emails use the default.' : undefined}
                    emptyAction="Import template"
                    onEmptyAction={openImport}
                >
                    <TCard>
                        {(rows ?? []).map((row, i) => {
                            const when = row.createdAt ? moment(row.createdAt).format('MMM D, YYYY') : '';
                            return (
                                <TRow
                                    key={row.id}
                                    index={i}
                                    name={row.name}
                                    meta={when ? `Imported ${when}` : 'Share email template'}
                                    date={when}
                                    status={null}
                                    onOpen={() => setPreview(row)}
                                    menuItems={[
                                        { label: 'Preview', onClick: () => setPreview(row) },
                                        { label: 'Delete…', onClick: () => setRemoving(row) },
                                    ]}
                                />
                            );
                        })}
                    </TCard>
                    <TFoot>
                        {count} {count === 1 ? 'template' : 'templates'}
                    </TFoot>
                </ListState>

                <div className="org-preview">
                    <Dialog
                        open={preview != null}
                        title={preview ? `Preview — ${preview.name}` : 'Preview'}
                        onClose={() => setPreview(null)}
                        footer={
                            <button type="button" className="tb-btn" onClick={() => setPreview(null)}>
                                Close
                            </button>
                        }
                    >
                        {preview?.body ? (
                            /* sandboxed: template HTML runs no scripts */
                            <iframe
                                className="org-frame"
                                title={`Email template preview: ${preview.name}`}
                                sandbox=""
                                srcDoc={preview.body}
                            />
                        ) : (
                            <p className="quiet-empty">This template has no HTML to preview.</p>
                        )}
                    </Dialog>
                </div>

                <Dialog
                    open={importOpen}
                    title="Import template"
                    onClose={closeImport}
                    onSubmit={submitImport}
                    footer={
                        <>
                            <button type="button" className="tb-btn" disabled={importing} onClick={closeImport}>
                                Cancel
                            </button>
                            <button type="submit" className="tb-new" style={{ marginLeft: 0 }} disabled={importing}>
                                {importing ? 'Importing…' : 'Import'}
                            </button>
                        </>
                    }
                >
                    <p style={{ margin: '0 0 12px' }}>
                        Templates change how outgoing share emails look for galleries that use them.
                    </p>
                    <Field label="Name" error={nameError}>
                        <TextInput
                            autoFocus
                            placeholder="Holiday Party 2026"
                            value={importName}
                            disabled={importing}
                            aria-invalid={nameError ? true : undefined}
                            onChange={(e) => setImportName(e.target.value)}
                        />
                    </Field>
                    <Field label="Template zip" help="index.html at the root, images in an images/ folder." error={fileError}>
                        <input
                            type="file"
                            className="org-file"
                            accept=".zip,application/zip"
                            disabled={importing}
                            aria-invalid={fileError ? true : undefined}
                            onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                        />
                    </Field>
                    {importError && (
                        <p className="field-e" role="alert" style={{ margin: '10px 0 0' }}>
                            {importError}
                        </p>
                    )}
                </Dialog>

                <ConfirmDialog
                    open={removing != null}
                    title="Delete template?"
                    body={
                        removing
                            ? `Delete “${removing.name}”? Galleries using it fall back to the default email. This can’t be undone.`
                            : ''
                    }
                    actionLabel="Delete"
                    onConfirm={() => removing && remove(removing)}
                    onClose={() => setRemoving(null)}
                />
                {toast.node}
            </div>
        </AreaShell>
    );
}
